import {
  EDIT_MESSAGE_STATUS,
  GROUP_TYPE,
  IMessage,
  IOrganizationUser,
  MESSAGE_STATUS,
} from "@home-health-notify/domain";
import { Box, Checkbox as SelectMessageCheckbox } from "@mui/material";
import React, { memo, useContext, useState } from "react";
import { DeleteConfirmationDialog } from "../messagePanelMenu/DeleteConfirmationDialog";
import UserImage from "../../common/user/UserImage";
import theme from "../../theme";
import { getMessageTypeComponent } from "../../utils/MessageUtils";
import {
  BubbleMenu,
  BubbleMenuIcon,
  MessageBubble,
  MessageBubbleWithAvatar,
  MessagePanelBubble,
  MessagePanelBubbleWithForwardOption,
} from "../MessagePanel.style";
import MessagePanelMenu from "../messagePanelMenu/MessagePanelMenu";
import MessageContentType from "../messageType/MessageContentType";
import { MessageFooter } from "./MessageFooter";
import MessageForwardedInfo from "./MessageForwardedInfo";
import MessageHighlightOverlay from "./MessageHighlightOverlay";
import {
  ACTIVE_MESSAGE_STATUS,
  getMessageStatus,
  IChatInfoAccess,
  MessagePanelActionsContext,
  useIsMessageSelected,
} from "hhn-commons";
import { AVATAR_SIZE } from "../../utils/AvatarUtils";

export interface MessagePanelItemType {
  chatInfoAccess: IChatInfoAccess;
  selectedOrgUser: IOrganizationUser;
  message: IMessage;
  openMediaDialog?: (message: IMessage) => void;
  messageBubbleFullWidth?: boolean;
  isForcedFallbackContent?: boolean;
  showMessageInfo?: boolean;
  disableActions?: boolean;
  hideAvatar?: boolean;
  shouldFocusOnMessage?: boolean;
  isForwardMessagesOn?: boolean;
  selectMessage: (message: IMessage) => void;
  hideContextMenu: boolean;
  isReplyPreview: boolean;
  footer?: React.ReactNode;
}

const MessagePanelItem = ({
  chatInfoAccess,
  selectedOrgUser,
  message,
  openMediaDialog,
  messageBubbleFullWidth = false,
  isForcedFallbackContent = false,
  showMessageInfo = false,
  disableActions = false,
  hideAvatar = false,
  shouldFocusOnMessage = false,
  isForwardMessagesOn = false,
  selectMessage,
  hideContextMenu = false,
  isReplyPreview,
  footer,
}: MessagePanelItemType) => {
  const messageData = message.getMessage();
  const { addToSelectedMessage } = useContext(MessagePanelActionsContext);
  const isChecked = useIsMessageSelected(messageData.id);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [messageToDelete, setMessageToDelete] = useState<IMessage | null>(null);
  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const closeMenu = () => {
    setAnchorEl(null);
  };

  const MessageTypeValue = messageData.type;
  const isSender = messageData.isSender;
  const isMessageDeleted = message.isDeleted();
  const isMessageTypeInfo = message.isInfoMessage();
  const isReminderMessage = message.isReminder();
  const canForwardMessage = message.canForward();
  const isVisibleIfDeleted = message.isVisibleIfDeleted();

  const showDeletedBubble = isMessageDeleted && !isVisibleIfDeleted;
  const isVisibleToPatient = message.isVisibleToPatient();
  const isVisibleToPhysician = message.isVisibleToPhysician();
  const isVisibleToPatientAndPhysician =
    message.isVisibleToPatientAndPhysician();

  const MessageTypeComponent = getMessageTypeComponent(
    MessageTypeValue,
    showDeletedBubble,
  );

  const isBroadcastMessage = message.isBroadcastMessage();
  const isPrivateWindow =
    chatInfoAccess.chatInfo.groupType === GROUP_TYPE.PRIVATE;

  const isShowBroadcastIcon = isPrivateWindow && isBroadcastMessage && isSender;

  const showAvatar =
    !hideAvatar &&
    !isBroadcastMessage &&
    !isPrivateWindow &&
    !isMessageTypeInfo;

  const isMessagePending = messageData.status === MESSAGE_STATUS.PENDING;
  const isMessageFailed = messageData.status === MESSAGE_STATUS.FAILED;

  const isEditPending = messageData.editStatus === EDIT_MESSAGE_STATUS.PENDING;
  const isEditFailed = messageData.editStatus === EDIT_MESSAGE_STATUS.FAILED;

  const isPending = isMessagePending || isEditPending;
  const isFailed = isMessageFailed || isEditFailed;

  const showMessageStatusIcon =
    isPending ||
    isFailed ||
    (isSender && isPrivateWindow && !isBroadcastMessage);
  let messageStatus: ACTIVE_MESSAGE_STATUS | undefined = undefined;

  if (showMessageStatusIcon) {
    messageStatus = getMessageStatus(
      messageData.visibleTo ?? [],
      messageData.chatId,
      isPending,
      isFailed,
    );
  }

  const onRetrySend =
    isEditFailed && message.canRetryEdit()
      ? () => message.retryEdit()
      : isMessageFailed && message.canRetrySend()
        ? () => message.retrySend()
        : undefined;

  const showContextMenu =
    !hideContextMenu &&
    !isMessageTypeInfo &&
    !isMessageDeleted &&
    !isForwardMessagesOn &&
    !isPending &&
    !isFailed;

  const defaultBackgroundColor = theme.palette.common.white;
  let bubbleBackgroundColor = isMessageDeleted
    ? (theme.message?.deleted ?? defaultBackgroundColor)
    : defaultBackgroundColor;
  if (!isMessageDeleted) {
    if (isMessageTypeInfo || isReminderMessage) {
      bubbleBackgroundColor = theme.palette.grey[200];
    } else if (isVisibleToPatientAndPhysician) {
      bubbleBackgroundColor =
        theme.message?.visibleToPatientAndPhysician ?? defaultBackgroundColor;
    } else if (isVisibleToPatient) {
      bubbleBackgroundColor =
        theme.message?.visibleToPatient ?? defaultBackgroundColor;
    } else if (isVisibleToPhysician) {
      bubbleBackgroundColor =
        theme.message?.visibleToPhysician ?? defaultBackgroundColor;
    }
  }

  const handleSelectedMessageClick = () => {
    if (canForwardMessage && isForwardMessagesOn) addToSelectedMessage(message);
  };

  return (
    <MessagePanelBubbleWithForwardOption>
      <MessagePanelBubble
        messageType={MessageTypeValue}
        isSender={isSender}
        key={messageData.id}
        onClick={handleSelectedMessageClick}
        isForwardMessagesOn={isForwardMessagesOn}
        isChecked={isChecked}
        data-testid="message-list-item"
      >
        {canForwardMessage && isForwardMessagesOn && (
          <SelectMessageCheckbox
            sx={{ marginLeft: "10px" }}
            disableRipple
            checked={isChecked}
          />
        )}
        <MessageHighlightOverlay
          shouldFocusOnMessage={shouldFocusOnMessage}
          shouldSelectMessage={isChecked}
        />
        <MessageBubbleWithAvatar
          isSender={isSender}
          messageBubbleFullWidth={messageBubbleFullWidth}
          showMessageInfo={showMessageInfo}
        >
          {!isForwardMessagesOn && showAvatar && (
            <UserImage
              userId={messageData.from}
              avatarSize={AVATAR_SIZE.SMALL}
            />
          )}
          <MessageBubble
            messageBubbleFullWidth={messageBubbleFullWidth}
            bubbleBackgroundColor={bubbleBackgroundColor}
            data-testid={isMessageDeleted && "deleted-message"}
          >
            {showContextMenu && (
              <BubbleMenu keepMenuVisible={Boolean(anchorEl)}>
                <Box onClick={openMenu} data-testid="context-menu-button">
                  <BubbleMenuIcon color="inherit" />
                </Box>
                {anchorEl && (
                  <MessagePanelMenu
                    closeMenu={closeMenu}
                    anchorEl={anchorEl}
                    message={message}
                    selectMessage={selectMessage}
                    onRequestDelete={setMessageToDelete}
                    chatInfoAccess={chatInfoAccess}
                  />
                )}
              </BubbleMenu>
            )}
            <MessageTypeComponent
              message={message}
              isSender={isSender}
              selectedOrgUser={selectedOrgUser}
              disableActions={disableActions}
              isReplyPreview={isReplyPreview}
              isForwardOn={isForwardMessagesOn}
            >
              {messageData.isForwarded && (
                <MessageForwardedInfo message={message} />
              )}
              <MessageContentType
                message={message}
                isSender={isSender}
                isForwardOn={isForwardMessagesOn}
                openMediaDialog={openMediaDialog}
                forcedFallbackContent={isForcedFallbackContent}
                disableActions={disableActions}
                chatInfoAccess={chatInfoAccess}
                isReplyPreview={false}
              />
            </MessageTypeComponent>
            {!isMessageTypeInfo && (
              <MessageFooter
                isMessageStarred={message.isStarred()}
                isShowBroadcastIcon={isShowBroadcastIcon}
                message={message}
                {...(messageStatus !== undefined ? { messageStatus } : {})}
                {...(onRetrySend ? { onRetrySend } : {})}
              />
            )}
            {footer}
          </MessageBubble>
        </MessageBubbleWithAvatar>
        <DeleteConfirmationDialog
          messageToDelete={messageToDelete}
          onClose={() => setMessageToDelete(null)}
        />
      </MessagePanelBubble>
    </MessagePanelBubbleWithForwardOption>
  );
};

export default memo(MessagePanelItem);
